/**
 * @file formatPercentage.ts
 * @brief Shared percentage formatting for consistent UI display across the app.
 * @author Nestbotics
 * @date 2026-06-19
 */

export interface FormatPercentageOptions {
  /** When true, appends a percent sign to the formatted value. */
  includeSymbol?: boolean;
  /** Fallback numeric string when value is missing or invalid. */
  fallback?: string;
}

/**
 * @brief Formats a percentage number with at most two digits after the decimal point.
 * @param value Percentage value (e.g. 88.904 for 88.904%).
 * @param options Formatting options.
 * @return Formatted percentage string, capped at two decimal places.
 */
export function formatPercentage(
  value: number | string | null | undefined,
  options: FormatPercentageOptions = {}
): string {
  const { includeSymbol = false, fallback = '0' } = options;

  const numeric = typeof value === 'string' ? Number(value) : value;
  if (numeric == null || !Number.isFinite(numeric)) {
    return includeSymbol ? `${fallback}%` : fallback;
  }

  const rounded = Math.round(numeric * 100) / 100;
  const formatted = rounded.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return includeSymbol ? `${formatted}%` : formatted;
}

/**
 * @brief Convenience wrapper that always includes the percent symbol.
 * @param value Percentage value.
 * @return Formatted string such as "88.9%" or "88.90%".
 */
export function formatPercent(value: number | string | null | undefined): string {
  return formatPercentage(value, { includeSymbol: true });
}
